import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Heart, Menu, X, Coins, Crown } from "lucide-react";
import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

const links = [
  { to: "/forum", label: "Forum" },
  { to: "/help", label: "Help Board" },
  { to: "/gallery", label: "Gallery" },
  { to: "/games", label: "Games" },
  { to: "/nurses", label: "Nurses" },
  { to: "/support", label: "Support" },
  { to: "/members", label: "Families" },
  { to: "/resources", label: "Resources" },
  { to: "/donate", label: "Donate" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const initials = user && user.name ? user.name.slice(0, 2).toUpperCase() : "AM";

  return (
    <header
      data-testid="main-navbar"
      className="sticky top-0 z-50 backdrop-blur-xl bg-[#FDFBF7]/80 border-b border-[#6A0DAD]/30"
    >
      <div className="max-w-6xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2">
          <span className="relative grid place-items-center w-9 h-9 rounded-full bg-[#6A0DAD] text-white">
            <Heart size={18} fill="white" />
            <Crown
              size={16}
              fill="#D4AF37"
              className="absolute -top-2.5 left-1/2 -translate-x-1/2 -rotate-[20deg] text-[#B8972E] drop-shadow"
            />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading font-bold text-lg text-[#1A202C] tracking-tight">A.M.I.R</span>
            <span className="font-heading text-[10px] text-[#6A0DAD] tracking-wide">A Miracle is Real</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(" ", "-")}`}
              className={`px-2.5 py-2 rounded-full text-sm font-heading font-medium transition-colors ${
                loc.pathname.startsWith(l.to)
                  ? "bg-white text-[#6B8EAC] shadow-sm"
                  : "text-[#4A5568] hover:text-[#1A202C]"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <>
              <Link
                to="/wallet"
                data-testid="nav-wallet-balance"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#D4AF37]/12 text-[#B8972E] text-sm font-heading font-semibold"
              >
                <Coins size={15} /> {user.amir_balance} AMIR
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="user-menu-trigger">
                    <Avatar className="w-9 h-9 border border-[#E2E8F0]">
                      <AvatarFallback className="bg-[#6B8EAC] text-white text-sm font-heading">
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem data-testid="menu-profile" onClick={() => nav("/profile")}>
                    My Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-wallet" onClick={() => nav("/wallet")}>
                    AMIR Wallet
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    data-testid="menu-logout"
                    onClick={async () => {
                      await logout();
                      nav("/");
                    }}
                  >
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <Link
              to="/auth"
              data-testid="nav-signin"
              className="bg-[#6B8EAC] text-white hover:bg-[#5A7B96] rounded-full px-6 py-2.5 text-sm font-heading font-semibold transition-colors"
            >
              Join the family
            </Link>
          )}
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden text-[#1A202C]"
          onClick={() => setOpen(!open)}
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-[#E2E8F0] bg-[#FDFBF7] px-5 py-4 space-y-1">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              className="block px-4 py-2.5 rounded-xl font-heading text-[#4A5568] hover:bg-white"
            >
              {l.label}
            </Link>
          ))}
          {user ? (
            <>
              <Link to="/wallet" onClick={() => setOpen(false)} className="block px-4 py-2.5 rounded-xl font-heading text-[#B8972E]">
                Wallet · {user.amir_balance} AMIR
              </Link>
              <Link to="/profile" onClick={() => setOpen(false)} className="block px-4 py-2.5 rounded-xl font-heading text-[#4A5568]">
                My Profile
              </Link>
              <button
                onClick={async () => { await logout(); setOpen(false); nav("/"); }}
                className="block w-full text-left px-4 py-2.5 rounded-xl font-heading text-[#4A5568]"
              >
                Sign out
              </button>
            </>
          ) : (
            <Link to="/auth" onClick={() => setOpen(false)} className="block px-4 py-2.5 rounded-xl font-heading text-white bg-[#6B8EAC] text-center">
              Join the family
            </Link>
          )}
        </div>
      )}
    </header>
  );
}
